/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { Context, MemoryUnits, TimeUnits } from './pg-config.consts';
import { TemplatedJSONSchema, UiSchema } from './pg-config.types';

export const schema = {
  type: 'object',
  additionalProperties: false,
  properties: {
    general: {
      title: 'General',
      type: 'object',
      additionalProperties: false,
      properties: {
        max_connections: {
          // Log scale?
          title: 'max_connections',
          type: 'integer',
          minimum: 100,
          maximum: {
            $eval: 'min(floor(ceil(cluster.memory * 1024 * 1024 / 3000)), 5000)',
          } as unknown as number,
          default: 100,
        },
        shared_buffers: {
          // Log scale
          title: 'shared_buffers[MB]',
          type: 'integer',
          minimum: 128,
          maximum: {
            $eval: 'max(floor(ceil(cluster.memory * 1024 / 2)), 128)',
          } as unknown as number,
          default: {
            $eval: 'max(floor(ceil(cluster.memory * 1024 * 0.25)), 128)',
          } as unknown as number,
        },
        temp_buffers: {
          // Log scale?
          title: 'temp_buffers[MB]',
          type: 'integer',
          minimum: 1,
          maximum: 1024,
          default: 8,
        },
        work_mem: {
          // Log scale?
          title: 'work_mem[MB]',
          type: 'integer',
          minimum: 1,
          maximum: {
            $eval: 'max(floor(ceil(cluster.memory * 1024 / 16)), 32)',
          } as unknown as number,
          default: 32,
        },
        maintenance_work_mem: {
          // Log scale?
          title: 'maintenance_work_mem[MB]',
          type: 'integer',
          minimum: 32,
          maximum: {
            $eval: 'max(floor(ceil(cluster.memory * 1024 / 10)), 128)',
          } as unknown as number,
          default: {
            $eval: 'min(floor(ceil(cluster.memory * 1024 / 10)), 128)',
          } as unknown as number,
        },
        max_worker_processes: {
          title: 'max_worker_processes',
          type: 'integer',
          minimum: 4,
          maximum: 128,
          default: {
            $eval: 'max(floor(cluster.cpu), 4)',
          } as unknown as number,
        },
        max_parallel_workers: {
          title: 'max_parallel_workers',
          type: 'integer',
          minimum: 4,
          maximum: 128,
          default: {
            $eval: 'max(floor(cluster.cpu), 4)',
          } as unknown as number,
        },
        max_parallel_maintenance_workers: {
          title: 'max_parallel_maintenance_workers',
          type: 'integer',
          minimum: 2,
          maximum: {
            $eval: 'max(ceil(cluster.cpu * 2),2)',
          } as unknown as number,
          default: 2,
        },
        max_parallel_workers_per_gather: {
          title: 'max_parallel_workers_per_gather',
          type: 'integer',
          minimum: 2,
          maximum: {
            $eval: 'max(ceil(cluster.cpu * 2),2)',
          } as unknown as number,
          default: 2,
        },
        old_snapshot_threshold: {
          title: 'old_snapshot_threshold[min]',
          type: 'integer',
          minimum: -1,
          maximum: 86400, // 60 days
          default: -1,
        },
        backend_flush_after: {
          title: 'backend_flush_after[kB]',
          type: 'integer',
          minimum: 0,
          maximum: 256 * 8,
          default: 0,
          multipleOf: 8,
        },
        synchronous_commit: {
          title: 'synchronous_commit',
          type: 'string',
          enum: ['off', 'on'],
          default: 'on',
        },
        wal_writer_delay: {
          // Log scale
          title: 'wal_writer_delay[ms]',
          type: 'integer',
          minimum: 1,
          maximum: 10000,
          default: 200,
        },
        wal_writer_flush_after: {
          // Log scale
          title: 'wal_writer_flush_after[MB]',
          type: 'integer',
          minimum: 0,
          maximum: 128,
          default: 1,
        },
        commit_delay: {
          // Log scale
          title: 'commit_delay',
          type: 'integer',
          minimum: 0,
          maximum: 100000,
          default: 0,
        },
        commit_siblings: {
          title: 'commit_siblings',
          type: 'integer',
          minimum: 1,
          maximum: 1000,
          default: 5,
        },
        checkpoint_timeout: {
          // Log scale
          title: 'checkpoint_timeout[min]',
          type: 'integer',
          minimum: 1,
          maximum: 1440,
          default: 15,
        },
        max_wal_size: {
          // Log scale
          title: 'max_wal_size[MB]',
          type: 'integer',
          minimum: 256,
          maximum: {
            $eval: 'min(256 * 1024, max(ceil(cluster.storage * 1024 / 5), 1024))',
          } as unknown as number,
          default: 1024,
        },
        checkpoint_completion_target: {
          title: 'checkpoint_completion_target',
          type: 'number',
          minimum: 0.0,
          maximum: 1.0,
          default: 0.75,
          multipleOf: 0.01,
        },
        checkpoint_flush_after: {
          // Log scale
          title: 'checkpoint_flush_after[kB]',
          type: 'integer',
          minimum: 0,
          maximum: 256 * 8,
          default: 32,
          multipleOf: 8,
        },
        checkpoint_warning: {
          // Log scale
          title: 'checkpoint_warning[sec]',
          type: 'integer',
          minimum: 10,
          maximum: 600,
          default: 30,
        },
        vacuum_defer_cleanup_age: {
          // Log scale
          title: 'vacuum_defer_cleanup_age',
          type: 'integer',
          minimum: 0,
          maximum: 1000000,
          default: 0,
        },
        max_standby_streaming_delay: {
          // Log scale
          title: 'max_standby_streaming_delay[sec]',
          type: 'integer',
          minimum: 1,
          maximum: 10000,
          default: 30,
        },
        wal_level: {
          title: 'wal_level',
          type: 'string',
          enum: ['replica', 'logical'],
        },
        enable_bitmapscan: {
          title: 'enable_bitmapscan',
          type: 'boolean',
          default: true,
        },
        enable_hashagg: {
          title: 'enable_hashagg',
          type: 'boolean',
          default: true,
        },
        enable_indexscan: {
          title: 'enable_indexscan',
          type: 'boolean',
          default: true,
        },
        enable_indexonlyscan: {
          title: 'enable_indexonlyscan',
          type: 'boolean',
          default: true,
        },
        enable_material: {
          title: 'enable_material',
          type: 'boolean',
          default: true,
        },
        enable_mergejoin: {
          title: 'enable_mergejoin',
          type: 'boolean',
          default: true,
        },
        enable_nestloop: {
          title: 'enable_nestloop',
          type: 'boolean',
          default: true,
        },
        enable_parallel_append: {
          title: 'enable_parallel_append',
          type: 'boolean',
          default: true,
        },
        enable_seqscan: {
          title: 'enable_seqscan',
          type: 'boolean',
          default: true,
        },
        enable_sort: {
          title: 'enable_sort',
          type: 'boolean',
          default: true,
        },
        enable_tidscan: {
          title: 'enable_tidscan',
          type: 'boolean',
          default: true,
        },
        enable_partitionwise_join: {
          title: 'enable_partitionwise_join',
          type: 'boolean',
          default: true,
        },
        enable_partitionwise_aggregate: {
          title: 'enable_partitionwise_aggregate',
          type: 'boolean',
          default: true,
        },
        enable_parallel_hash: {
          title: 'enable_parallel_hash',
          type: 'boolean',
          default: true,
        },
        enable_partition_pruning: {
          title: 'enable_partition_pruning',
          type: 'boolean',
          default: true,
        },
      },
    },
    optimizerSettings: {
      title: 'Optimizer settings',
      type: 'object',
      additionalProperties: false,
      properties: {
        seq_page_cost: {
          // Log scale
          title: 'seq_page_cost',
          type: 'number',
          minimum: 0.1,
          maximum: 1000.0,
          default: 1.0,
          multipleOf: 0.1,
        },
        random_page_cost: {
          // Log scale
          title: 'random_page_cost',
          type: 'number',
          minimum: 0.1,
          maximum: 1000.0,
          default: 4.0,
          multipleOf: 0.1,
        },
        cpu_tuple_cost: {
          // Log scale
          title: 'cpu_tuple_cost',
          type: 'number',
          minimum: 0.0001,
          maximum: 10.0,
          default: 0.01,
          multipleOf: 0.0001,
        },
        cpu_index_tuple_cost: {
          // Log scale
          title: 'cpu_index_tuple_cost',
          type: 'number',
          minimum: 0.0001,
          maximum: 10.0,
          default: 0.005,
          multipleOf: 0.0001,
        },
        cpu_operator_cost: {
          // Log scale
          title: 'cpu_operator_cost',
          type: 'number',
          minimum: 0.0001,
          maximum: 10.0,
          default: 0.0025,
          multipleOf: 0.0001,
        },
        parallel_tuple_cost: {
          // Log scale
          title: 'parallel_tuple_cost',
          type: 'number',
          minimum: 0.0001,
          maximum: 10.0,
          default: 0.1,
          multipleOf: 0.0001,
        },
        parallel_setup_cost: {
          // Log scale
          title: 'parallel_setup_cost',
          type: 'integer',
          minimum: 1,
          maximum: 100000,
          default: 1000,
        },
        jit_above_cost: {
          // Log scale
          title: 'jit_above_cost',
          type: 'integer',
          minimum: 1000,
          maximum: 1000000000,
          default: 100000,
        },
        jit_inline_above_cost: {
          // Log scale
          title: 'jit_inline_above_cost',
          type: 'integer',
          minimum: 1000,
          maximum: 1000000000,
          default: 500000,
        },
        jit_optimize_above_cost: {
          // Log scale
          title: 'jit_optimize_above_cost',
          type: 'integer',
          minimum: 1000,
          maximum: 1000000000,
          default: 500000,
        },
        min_parallel_table_scan_size: {
          // Log scale
          title: 'min_parallel_table_scan_size[MB]',
          type: 'integer',
          minimum: 1,
          maximum: 2048,
          default: 8,
        },
        min_parallel_index_scan_size: {
          // Log scale
          title: 'min_parallel_index_scan_size[kB]',
          type: 'integer',
          minimum: 128,
          maximum: 2048 * 1024,
          default: 512,
          multipleOf: 8,
        },
        effective_cache_size: {
          // Log scale
          title: 'effective_cache_size[MB]',
          type: 'integer',
          minimum: 128,
          maximum: {
            $eval: 'max(floor(ceil(cluster.memory * 1024 * 2)), 128)',
          } as unknown as number,
          default: {
            $eval: 'max(floor(ceil(cluster.memory * 1024 * 0.75)), 128)',
          } as unknown as number,
        },
        geqo: {
          title: 'geqo',
          type: 'boolean',
          default: true,
        },
        geqo_threshold: {
          title: 'geqo_threshold',
          type: 'integer',
          minimum: 5,
          maximum: 100,
          default: 12,
        },
        geqo_effort: {
          title: 'geqo_effort',
          type: 'integer',
          minimum: 1,
          maximum: 10,
          default: 5,
        },
        geqo_pool_size: {
          title: 'geqo_pool_size',
          type: 'integer',
          minimum: 0,
          maximum: 1000,
          default: 0,
        },
        geqo_generations: {
          title: 'geqo_generations',
          type: 'integer',
          minimum: 0,
          maximum: 1000,
          default: 0,
        },
        geqo_selection_bias: {
          title: 'geqo_selection_bias',
          type: 'number',
          minimum: 1.5,
          maximum: 2.0,
          multipleOf: 0.1,
          default: 2.0,
        },
        geqo_seed: {
          title: 'geqo_seed',
          type: 'number',
          minimum: 0.0,
          maximum: 1.0,
          default: 0.0,
          multipleOf: 0.01,
        },
        default_statistics_target: {
          title: 'default_statistics_target',
          type: 'integer',
          minimum: 1,
          maximum: 10000,
          default: 100,
        },
        cursor_tuple_fraction: {
          title: 'cursor_tuple_fraction',
          type: 'number',
          minimum: 0.0,
          maximum: 1.0,
          default: 0.1,
          multipleOf: 0.01,
        },
        from_collapse_limit: {
          title: 'from_collapse_limit',
          type: 'integer',
          minimum: 3,
          maximum: 100,
          default: 8,
        },
        join_collapse_limit: {
          title: 'join_collapse_limit',
          type: 'integer',
          minimum: 1,
          maximum: 100,
          default: 8,
        },
        constraint_exclusion: {
          title: 'constraint_exclusion',
          type: 'string',
          enum: ['on', 'off', 'partition'],
          default: 'partition',
        },
        force_parallel_mode: {
          title: 'force_parallel_mode',
          type: 'boolean',
          default: false,
        },
        jit: {
          title: 'jit',
          type: 'boolean',
          default: false,
        },
      },
    },
    logfiles: {
      title: 'Logfiles',
      type: 'object',
      additionalProperties: false,
      properties: {
        log_autovacuum_min_duration: {
          title: `log_autovacuum_min_duration[${TimeUnits.Milliseconds}]`,
          type: 'integer',
          minimum: -1,
          maximum: 2147483647,
        },
        log_checkpoints: {
          title: `log_checkpoints`,
          type: 'boolean',
        },
        log_connections: {
          title: `log_connections`,
          type: 'boolean',
        },
        log_disconnections: {
          title: `log_disconnections`,
          type: 'boolean',
        },
        log_lock_waits: {
          title: `log_lock_waits`,
          type: 'boolean',
        },
        log_min_duration_statement: {
          title: `log_min_duration_statement[${TimeUnits.Milliseconds}]`,
          type: 'integer',
          minimum: -1,
          maximum: 500000,
          default: -1,
        },
        log_rotation_size: {
          title: `log_rotation_size[${MemoryUnits.Megabytes}]`,
          type: 'integer',
          minimum: 0,
          maximum: 2097151,
          default: {
            $eval: 'ceil(cluster.storage * 1024 * 0.05)',
          } as unknown as number,
        },
        log_statement: {
          title: 'log_statement',
          type: 'string',
          enum: ['none', 'ddl', 'mod', 'all'],
        },
        log_temp_files: {
          title: `log_temp_files[${MemoryUnits.Kilobytes}]`,
          type: 'integer',
          minimum: -1,
          maximum: 2147483647,
        },
      },
    },
    statsCollection: {
      title: 'Statistics collection',
      type: 'object',
      additionalProperties: false,
      properties: {
        track_functions: {
          title: 'track_functions',
          type: 'string',
          enum: ['none', 'pl', 'all'],
          default: 'none',
        },
        track_io_timing: {
          title: 'track_io_timing',
          type: 'boolean',
          default: false,
        },
      },
    },
    autovacuum: {
      title: 'Autovacuum',
      type: 'object',
      additionalProperties: false,
      properties: {
        autovacuum_max_workers: {
          title: 'autovacuum_max_workers',
          type: 'integer',
          minimum: 3,
          maximum: 24,
          default: 3,
        },
        autovacuum_vacuum_threshold: {
          // Log scale
          title: 'autovacuum_vacuum_threshold',
          type: 'integer',
          minimum: 50,
          maximum: 10000,
          default: 2000,
        },
        autovacuum_analyze_threshold: {
          // Log scale
          title: 'autovacuum_analyze_threshold',
          type: 'integer',
          minimum: 50,
          maximum: 10000,
          default: 2000,
        },
        autovacuum_vacuum_scale_factor: {
          title: 'autovacuum_vacuum_scale_factor',
          type: 'number',
          minimum: 0.01,
          maximum: 2.0,
          multipleOf: 0.01,
          default: 0.05,
        },
        autovacuum_analyze_scale_factor: {
          title: 'autovacuum_analyze_scale_factor',
          type: 'number',
          minimum: 0.01,
          maximum: 2.0,
          multipleOf: 0.01,
          default: 0.1,
        },
        autovacuum_vacuum_cost_delay: {
          title: 'autovacuum_vacuum_cost_delay[ms]',
          type: 'integer',
          minimum: 0,
          maximum: 100,
          default: 10,
        },
      },
    },
    miscellaneous: {
      title: 'Miscellaneous',
      type: 'object',
      additionalProperties: false,
      properties: {
        default_transaction_isolation: {
          title: 'default_transaction_isolation',
          type: 'string',
          enum: ['read committed', 'repeatable read'],
          default: 'read committed',
        },
        statement_timeout: {
          // Log scale
          title: 'statement_timeout[ms]',
          type: 'integer',
          minimum: 0,
          maximum: 1000000000,
          default: 86400000,
        },
        idle_in_transaction_session_timeout: {
          // Log scale
          title: 'idle_in_transaction_session_timeout[ms]',
          type: 'integer',
          minimum: 0,
          maximum: 1000000000,
          default: 86400000,
        },
        vacuum_cleanup_index_scale_factor: {
          title: 'vacuum_cleanup_index_scale_factor',
          type: 'number',
          minimum: 0.1,
          maximum: 1,
          multipleOf: 0.1,
          default: 0.1,
        },
        gin_pending_list_limit: {
          // Log scale
          title: 'gin_pending_list_limit[MB]',
          type: 'integer',
          minimum: 1,
          maximum: 1024,
          default: 4,
        },
        deadlock_timeout: {
          title: 'deadlock_timeout[sec]',
          type: 'integer',
          minimum: 1,
          maximum: 10,
          default: 1,
        },
        max_locks_per_transaction: {
          // Log scale
          title: 'max_locks_per_transaction',
          type: 'integer',
          minimum: 10,
          maximum: 10000,
          default: 64,
        },
        max_pred_locks_per_transaction: {
          // Log scale
          title: 'max_pred_locks_per_transaction',
          type: 'integer',
          minimum: 10,
          maximum: 10000,
          default: 64,
        },
      },
    },
  },
} as const satisfies TemplatedJSONSchema;

export const uiSchema = {
  general: {
    'ui:field': 'collapsible',
    collapse: {
      field: 'ObjectField',
    },
    max_connections: {
      'ui:options': { context: Context.Postmaster },
    },
    shared_buffers: {
      'ui:options': {
        context: Context.Postmaster,
        unit: MemoryUnits.Megabytes,
      },
    },
    temp_buffers: {
      'ui:options': {
        context: Context.User,
        unit: MemoryUnits.Megabytes,
      },
    },
    work_mem: {
      'ui:options': {
        context: Context.User,
        unit: MemoryUnits.Megabytes,
      },
    },
    maintenance_work_mem: {
      'ui:options': {
        context: Context.User,
        unit: MemoryUnits.Megabytes,
      },
    },
    max_worker_processes: {
      'ui:options': { context: Context.Postmaster },
    },
    max_parallel_workers: { 'ui:options': { context: Context.User } },
    max_parallel_maintenance_workers: {
      'ui:options': { context: Context.User },
    },
    max_parallel_workers_per_gather: {
      'ui:options': { context: Context.User },
    },
    old_snapshot_threshold: {
      'ui:options': {
        context: Context.Postmaster,
        unit: TimeUnits.Minutes,
      },
    },
    backend_flush_after: {
      'ui:options': {
        context: Context.User,
        unit: MemoryUnits.Kilobytes,
      },
    },
    synchronous_commit: { 'ui:options': { context: Context.User } },
    wal_writer_delay: {
      'ui:options': {
        context: Context.Sighup,
        unit: TimeUnits.Milliseconds,
      },
    },
    wal_writer_flush_after: {
      'ui:options': {
        context: Context.Sighup,
        unit: MemoryUnits.Megabytes,
      },
    },
    commit_delay: { 'ui:options': { context: Context.Superuser } },
    commit_siblings: { 'ui:options': { context: Context.User } },
    checkpoint_timeout: {
      'ui:options': {
        context: Context.Sighup,
        unit: TimeUnits.Minutes,
      },
    },
    max_wal_size: {
      'ui:options': {
        context: Context.Sighup,
        unit: MemoryUnits.Megabytes,
      },
    },
    checkpoint_completion_target: {
      'ui:options': { context: Context.Sighup },
    },
    checkpoint_flush_after: {
      'ui:options': {
        context: Context.Sighup,
        unit: MemoryUnits.Kilobytes,
      },
    },
    checkpoint_warning: {
      'ui:options': {
        context: Context.Sighup,
        unit: TimeUnits.Seconds,
      },
    },
    vacuum_defer_cleanup_age: {
      'ui:options': { context: Context.Sighup },
    },
    max_standby_streaming_delay: {
      'ui:options': {
        context: Context.Sighup,
        unit: TimeUnits.Seconds,
      },
    },
    wal_level: { 'ui:options': { context: Context.Postmaster } },
    enable_bitmapscan: { 'ui:options': { context: Context.User } },
    enable_hashagg: { 'ui:options': { context: Context.User } },
    enable_indexscan: { 'ui:options': { context: Context.User } },
    enable_indexonlyscan: { 'ui:options': { context: Context.User } },
    enable_material: { 'ui:options': { context: Context.User } },
    enable_mergejoin: { 'ui:options': { context: Context.User } },
    enable_nestloop: { 'ui:options': { context: Context.User } },
    enable_parallel_append: {
      'ui:options': { context: Context.User },
    },
    enable_seqscan: { 'ui:options': { context: Context.User } },
    enable_sort: { 'ui:options': { context: Context.User } },
    enable_tidscan: { 'ui:options': { context: Context.User } },
    enable_partitionwise_join: {
      'ui:options': { context: Context.User },
    },
    enable_partitionwise_aggregate: {
      'ui:options': { context: Context.User },
    },
    enable_parallel_hash: { 'ui:options': { context: Context.User } },
    enable_partition_pruning: {
      'ui:options': { context: Context.User },
    },
  },
  optimizerSettings: {
    'ui:field': 'collapsible',
    collapse: {
      field: 'ObjectField',
    },
    seq_page_cost: { 'ui:options': { context: Context.User } },
    random_page_cost: { 'ui:options': { context: Context.User } },
    cpu_tuple_cost: { 'ui:options': { context: Context.User } },
    cpu_index_tuple_cost: { 'ui:options': { context: Context.User } },
    cpu_operator_cost: { 'ui:options': { context: Context.User } },
    parallel_tuple_cost: { 'ui:options': { context: Context.User } },
    parallel_setup_cost: { 'ui:options': { context: Context.User } },
    jit_above_cost: { 'ui:options': { context: Context.User } },
    jit_inline_above_cost: {
      'ui:options': { context: Context.User },
    },
    jit_optimize_above_cost: {
      'ui:options': { context: Context.User },
    },
    min_parallel_table_scan_size: {
      'ui:options': {
        context: Context.User,
        unit: MemoryUnits.Megabytes,
      },
    },
    min_parallel_index_scan_size: {
      'ui:options': {
        context: Context.User,
        unit: MemoryUnits.Kilobytes,
      },
    },
    effective_cache_size: {
      'ui:options': {
        context: Context.User,
        unit: MemoryUnits.Megabytes,
      },
    },
    geqo: { 'ui:options': { context: Context.User } },
    geqo_threshold: { 'ui:options': { context: Context.User } },
    geqo_effort: { 'ui:options': { context: Context.User } },
    geqo_pool_size: { 'ui:options': { context: Context.User } },
    geqo_generations: { 'ui:options': { context: Context.User } },
    geqo_selection_bias: { 'ui:options': { context: Context.User } },
    geqo_seed: { 'ui:options': { context: Context.User } },
    default_statistics_target: {
      'ui:options': { context: Context.User },
    },
    cursor_tuple_fraction: {
      'ui:options': { context: Context.User },
    },
    from_collapse_limit: { 'ui:options': { context: Context.User } },
    join_collapse_limit: { 'ui:options': { context: Context.User } },
    constraint_exclusion: { 'ui:options': { context: Context.User } },
    force_parallel_mode: { 'ui:options': { context: Context.User } },
    jit: { 'ui:options': { context: Context.User } },
  },
  logfiles: {
    'ui:field': 'collapsible',
    collapse: {
      field: 'ObjectField',
    },
    log_autovacuum_min_duration: {
      'ui:options': {
        context: Context.Sighup,
        unit: TimeUnits.Milliseconds,
      },
    },
    log_checkpoints: {
      'ui:options': {
        context: Context.Sighup,
      },
    },
    log_min_duration_statement: {
      'ui:options': {
        context: Context.Superuser,
        unit: TimeUnits.Milliseconds,
      },
    },
    log_rotation_size: {
      'ui:options': {
        context: Context.Sighup,
        unit: MemoryUnits.Megabytes,
      },
    },
    log_temp_files: {
      'ui:options': {
        context: Context.Superuser,
        unit: MemoryUnits.Kilobytes,
      },
    },
    log_statement: { 'ui:options': { context: Context.Superuser } },
    log_lock_waits: { 'ui:options': { context: Context.Superuser } },
    log_connections: {
      'ui:options': { context: Context.SuperuserBackend },
    },
    log_disconnections: {
      'ui:options': { context: Context.SuperuserBackend },
    },
  },
  statsCollection: {
    'ui:field': 'collapsible',
    collapse: {
      field: 'ObjectField',
    },
    track_functions: { 'ui:options': { context: Context.Superuser } },
    track_io_timing: { 'ui:options': { context: Context.Superuser } },
  },
  autovacuum: {
    'ui:field': 'collapsible',
    collapse: {
      field: 'ObjectField',
    },
    autovacuum_max_workers: {
      'ui:options': { context: Context.Postmaster },
    },
    autovacuum_vacuum_threshold: {
      'ui:options': { context: Context.Sighup },
    },
    autovacuum_analyze_threshold: {
      'ui:options': { context: Context.Sighup },
    },
    autovacuum_vacuum_scale_factor: {
      'ui:options': { context: Context.Sighup },
    },
    autovacuum_analyze_scale_factor: {
      'ui:options': { context: Context.Sighup },
    },
    autovacuum_vacuum_cost_delay: {
      'ui:options': {
        context: Context.Sighup,
        unit: TimeUnits.Milliseconds,
      },
    },
  },
  miscellaneous: {
    'ui:field': 'collapsible',
    collapse: {
      field: 'ObjectField',
    },
    default_transaction_isolation: {
      'ui:options': { context: Context.User },
    },
    statement_timeout: {
      'ui:options': {
        context: Context.User,
        unit: TimeUnits.Milliseconds,
      },
    },
    idle_in_transaction_session_timeout: {
      'ui:options': {
        context: Context.User,
        unit: TimeUnits.Milliseconds,
      },
    },
    vacuum_cleanup_index_scale_factor: {
      'ui:options': { context: Context.User },
    },
    gin_pending_list_limit: {
      'ui:options': {
        context: Context.User,
        unit: MemoryUnits.Megabytes,
      },
    },
    deadlock_timeout: {
      'ui:options': {
        context: Context.Superuser,
        unit: TimeUnits.Seconds,
      },
    },
    max_locks_per_transaction: {
      'ui:options': { context: Context.Postmaster },
    },
    max_pred_locks_per_transaction: {
      'ui:options': { context: Context.Postmaster },
    },
  },
} as const satisfies UiSchema<typeof schema>;
